import { Box, Divider, Grid, Tab, Tabs } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer.tsx";
import type { ConditionField } from "../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import { selectSorts, selectTabularFields } from "../../../../store/selectors/fieldsStateSelectors";
import { selectEditDisabled, selectSelectedBuilderTab } from "../../../../store/selectors/uiSettingsSelectors.ts";
import { fieldsStateActions } from "../../../../store/thunks/fieldStatesThunks.ts";
import type { BuilderTab } from "../../../../store/types/uiSettingsState.types.ts";
import { createUiSettingsAction } from "../../../../store/utils/storeUtils.ts";
import TableDragLayer from "../../common/table/TableDragLayer.tsx";
import { getAvailableSortFields } from "../../common/utilities/sortFieldStateHelper.ts";
import ConditionsDropFieldsContainer from "../../pivot/table/conditions/ConditionsDropFieldsContainer.tsx";
import SettingsContainer from "../../tabular/settings/SettingsContainer.tsx";
import FormatContainer from "../../tabular/table/FormatContainer.tsx";
import SortDropsFieldsContainer from "../../tabular/table/SortDropsFieldsContainer.tsx";
import TabularFieldsDropContainer from "../../tabular/table/TabularFieldsDropContainer.tsx";
import CustomId from "./fields/CustomId.tsx";

const TableComponent = () => {
  const dispatch = useAppDispatch();
  const selectedTab = useSelector(selectSelectedBuilderTab);
  const sortingAreaValues = useSelector(selectSorts);
  const fieldsAreaValues = useSelector(selectTabularFields);
  const editDisabled = useSelector(selectEditDisabled);

  const availableSortFields = useMemo(
    () => getAvailableSortFields(fieldsAreaValues, sortingAreaValues),
    [fieldsAreaValues, sortingAreaValues]
  );

  const renderCustomId = useCallback(
    (field: ConditionField) => {
      return (
        <CustomId
          customId={field.config.customId}
          defaultId={field.config.guid}
          updateCustomId={(customId) =>
            dispatch(
              fieldsStateActions.updateCondition({
                field: field,
                changes: { config: { ...field.config, customId: customId } },
              })
            )
          }
        />
      );
    },
    [dispatch]
  );

  return (
    <Box sx={{ display: "flex", flex: 1, flexDirection: "column", gap: "1rem", mt: "1rem" }}>
      <Grid container sx={{ px: "1.3rem" }}>
        <Tabs
          value={selectedTab}
          sx={{ borderBottom: "none" }}
          onChange={(_, value: BuilderTab) => dispatch(createUiSettingsAction("setTab", value))}
        >
          <Tab value="Fields" label="Fields" />
          <Tab value="Settings" label="Settings" />
          <Tab value="Format" label="Format" />
        </Tabs>
      </Grid>
      <Divider sx={{ mt: -2 }} />
      {selectedTab === "Fields" && (
        <ScrollableFlexContainer
          scrollContainerSx={{ display: "flex", flexDirection: "column", gap: "1.5rem", px: "1.3rem" }}
        >
          <ConditionsDropFieldsContainer
            disableEqualitySelection={true}
            disableReferenceDateSelection={true}
            renderCustomId={renderCustomId}
            readonlyMode={editDisabled}
          />
          <TabularFieldsDropContainer isApiReport={true} readonlyMode={editDisabled} />
          <SortDropsFieldsContainer availableFields={availableSortFields} readonlyMode={editDisabled} />
          <TableDragLayer />
        </ScrollableFlexContainer>
      )}
      {selectedTab === "Settings" && <SettingsContainer isApiReport={true} readonlyMode={editDisabled} />}
      {selectedTab === "Format" && <FormatContainer readonlyMode={editDisabled} />}
    </Box>
  );
};

export default TableComponent;
